import type { QueryFunctionContext } from '@tanstack/react-query';
import { gqlClient } from '@/utilities/graphQLClient';
import { gql } from '@/utilities/gql';

interface REIT {
  NAV: number;
  NAVCount: number;
}

interface DividendDiscount {
  ddm_growth: number; // TODO: Should be also | null;
  ddm_source: string; // TODO: Should be also | null;
  dps: number; // TODO: Should be also | null;
  expected_growth: number; // TODO: Should be also | null;
  payout: number; // TODO: Should be also | null;
  roe: number; // TODO: Should be also | null;
}

interface ExcessReturns {
  stable_book_value: number;
  return_on_equity_avg: number;
  stable_eps: number;
  stable_eps_source: string;
  stable_book_value_source: string;
  book_value: number;
  equity_cost: number;
  excess_return: number;
}

interface Stage {
  data: number;
  discounted: number;
  source: string;
}

interface TwoStageFCF {
  affo_estimates: number;
  growth_cagr_5y: number;
  scale: string;
  scale_numeric: number;
  shares_outstanding: number;
  fcf_data: Record<number, number>;
  fcf_count: Record<number, number>;
  fcf_ltm: number;
  first_stage: Record<number, Stage>;
  annualized_adjust_free_cash_flow_time_series: Record<number, number>;
  capital_to_revenue_ratio_3yr_avg: number;
  combined_free_cash_flow_time_series: Record<number, number>;
}

interface IntrinsicValue {
  model: string;
  unlevered_beta: number;
  de: number;
  tax_rate: number;
  equity_premium: number;
  risk_free_rate: number;
  levered_beta_actual: number;
  levered_beta: number;
  cost_of_equity: number;
  adr_per_share: number;
  pv_5y: number;
  terminal_value: number;
  pvtv: number;
  npv: number;
  npv_per_share_reported_currency?: number;
  REIT: REIT | null;
  dividend_discount: DividendDiscount;
  excess_returns: ExcessReturns | null;
  two_stage_fcf: TwoStageFCF | null;
}

interface Listing {
  currency_iso: string;
  currency_symbol: string;
  exchange_country_iso: string;
  exchange_country_iso3: string;
  exchange_currency_iso: string;
  exchange_currency_symbol: string;
  exchange_importance_level: number;
  exchange_name: string;
  exchange_symbol: string;
  major_currency: true;
  primary_security: false;
  primary_trading_item: true;
  security_id: number;
  security_name: string;
  security_type_id: number;
  start_date: number;
  ticker_symbol: string;
  trading_item_id: number;
}

interface CurrencyInfo {
  primary_trading_item_currency_iso: string;
  primary_trading_item_currency_symbol: string;
  reporting_currency_iso: string;
  reporting_currency_symbol: string;
  reporting_unit: number;
  reporting_unit_abs: number;
  reporting_unit_text: string;
  reporting_unit_text_abs: string;
  trading_item_currency_iso: string;
  trading_item_currency_symbol: string;
}

interface MarketCap {
  listing: number;
  market_cap_band: string;
  original_market_cap: number;
  primary: number;
  relative_listing_shares_outstanding: number;
  reported: number;
  shares_outstanding: number;
  shares_outstanding_common_equity: number;
  total_enterprise_value_reported: number;
  trading_item_to_primary_ratio: number;
  usd: number;
}

export interface CompanyInterface {
  id: string;
  tickerSymbol: string;
  exchangeSymbol: string;
  exchangeCountryISO: string;
  name: string;
  uniqueSymbol: string;
  analysisMisc: {
    analystCount: number;
    analystFcfCountAverage: number;
  };
  data: {
    currencyInfo: CurrencyInfo;
    listings: Record<number, Listing>;
    marketCap: MarketCap;
  };
  analysisPast: {
    lastEarningsUpdateAnnual: number;
  };
  analysisValue: {
    intrinsicValue: IntrinsicValue;
    intrinsicDiscount: number;
    lastSharePrice: number;
    npvPerShare: number;
  };
  info: {
    secondaryIndustryName: string;
  };
}

const QUERY = gql(`
  query getDCFRawDate($companyId: String!) {
    Company(id: $companyId) {
      id
      tickerSymbol
      exchangeSymbol
      exchangeCountryISO
      name
      uniqueSymbol
      analysisMisc {
        analystCount
        analystFcfCountAverage
      }
      data {
        currencyInfo
        listings
        marketCap
      }
      analysisPast {
        lastEarningsUpdateAnnual
      }
      analysisValue {
        intrinsicValue
        intrinsicDiscount
        lastSharePrice
        npvPerShare
      }
      info {
        secondaryIndustryName
      }
    }
  }
`);

export default async function queryFn({
  queryKey: [_key, companyId],
}: QueryFunctionContext<[string, string]>) {
  const { Company } = await gqlClient.request(QUERY, {
    companyId,
  });

  // The response type contains maybe case and is more specific while the schema is unknown
  // The graph schema needs updating + don't want to update all maybe cases
  // Casting it now to avoid breaking the type checking
  return Company as unknown as CompanyInterface;
}
